/**
 * La ficha del trabajador en el portal: la versión resumida del inicio y las
 * piezas que reusa «Mi ficha» (plan del portal, fase 3).
 *
 * ⚠️ Es de **consulta** (D3): nada se edita acá. La cuenta bancaria ya llega
 * enmascarada del servidor y el sueldo base se oculta con `<Monto>` (D5).
 *
 * El encabezado va sobre la tinta de la marca y no sobre vidrio: es el único
 * bloque de color sólido y el texto blanco sobre la tinta da 17,9:1. En oscuro
 * la tinta se confunde con el canvas, así que lleva un borde tenue (el mismo
 * arreglo que el sidebar).
 */
import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import { GlassPanel } from '@/components/ds/GlassPanel'
import { Avatar } from '@/components/ds/Avatar'
import { Monto } from '@/components/ds/Monto'
import { useMoney } from '@/lib/locale'
import { usePortalBase, type PortalFicha } from '../hooks/useEmployeePortal'
import { antiguedadDesde, fechaCorta, o, uf } from './formatos'

export function Dato({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold text-content-muted">{rotulo}</dt>
      <dd className="mt-0.5 text-sm font-medium text-content break-words">{children ?? '—'}</dd>
    </div>
  )
}

export function EncabezadoFicha({ ficha, grande = false, acciones }: {
  // Solo lo que dibuja: así sirve también para la ficha de jefatura, que no trae
  // los bloques personal ni previsional.
  ficha: Pick<PortalFicha, 'nombre' | 'photo_url' | 'cargo' | 'trabajo'>; grande?: boolean; acciones?: ReactNode
}) {
  const lugar = [ficha.trabajo.area, ficha.trabajo.division].filter(Boolean).join(' · ')
  return (
    <div
      className={`rounded-card bg-[rgb(var(--brand-tinta))] text-white dark:border dark:border-white/10 flex ${
        grande ? 'flex-col sm:flex-row sm:items-center gap-6 p-6 sm:p-8' : 'items-center gap-4 p-5'
      }`}
    >
      <Avatar nombre={ficha.nombre} fotoUrl={ficha.photo_url} tamano={grande ? 'xl' : 'lg'} decorativa />
      <div className="flex-1 min-w-0">
        <p className={`${grande ? 'text-3xl' : 'text-lg'} font-bold leading-tight`}>{ficha.nombre}</p>
        {ficha.cargo && <p className="mt-1 text-sm text-white/80">{ficha.cargo}</p>}
        {lugar && <p className="text-sm text-white/70">{lugar}</p>}
        {grande && (
          <div className="flex flex-wrap gap-2 mt-3">
            {ficha.trabajo.contrato && <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">{ficha.trabajo.contrato}</span>}
            {ficha.trabajo.ingreso && (
              <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">
                Desde {fechaCorta(ficha.trabajo.ingreso)} · {antiguedadDesde(ficha.trabajo.ingreso)}
              </span>
            )}
          </div>
        )}
      </div>
      {acciones}
    </div>
  )
}

export function SueldoBase({ ficha }: { ficha: PortalFicha }) {
  const money = useMoney()
  return ficha.prevision.sueldo_base ? <Monto>{money(ficha.prevision.sueldo_base)}</Monto> : <>—</>
}

/**
 * El plan de salud pactado con la Isapre, en UF. Se oculta con `<Monto>` igual
 * que el sueldo (D5): el valor del plan también es un dato de remuneración.
 * Solo viaja para Isapre con plan cargado; la fila no se dibuja sin él.
 */
export function PlanSalud({ ficha }: { ficha: PortalFicha }) {
  return ficha.prevision.plan_salud_uf ? <Monto>{uf(ficha.prevision.plan_salud_uf)}</Monto> : <>—</>
}

export function FichaResumida({ ficha }: { ficha: PortalFicha }) {
  const base = usePortalBase()
  const { personales: p, trabajo: t, prevision: v } = ficha
  return (
    <GlassPanel as="section" aria-label="Mi ficha" padded={false} className="overflow-hidden">
      <EncabezadoFicha ficha={ficha} />
      <div className="p-5 flex flex-col gap-5">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-wide text-content-muted mb-2">Contacto</h2>
          <dl className="grid grid-cols-2 gap-3">
            <Dato rotulo="RUT">{p.rut}</Dato>
            <Dato rotulo="Teléfono">{o(p.telefono)}</Dato>
            <div className="col-span-2"><Dato rotulo="Correo">{o(p.correo)}</Dato></div>
          </dl>
        </div>
        <div>
          <h2 className="text-xs font-bold uppercase tracking-wide text-content-muted mb-2">Mi trabajo</h2>
          <dl className="grid grid-cols-2 gap-3">
            <Dato rotulo="Jefatura">{o(t.jefatura)}</Dato>
            <Dato rotulo="Centro de costo">{o(t.centro_de_costo)}</Dato>
            <Dato rotulo="Contrato">{o(t.contrato)}</Dato>
            <Dato rotulo="Jornada">{t.jornada_semanal ? `${Number(t.jornada_semanal)} h semanales` : '—'}</Dato>
            <div className="col-span-2">
              <Dato rotulo="Ingreso">{t.ingreso ? `${fechaCorta(t.ingreso)} (${antiguedadDesde(t.ingreso)})` : '—'}</Dato>
            </div>
          </dl>
        </div>
        <div>
          <h2 className="text-xs font-bold uppercase tracking-wide text-content-muted mb-2">Previsión y pago</h2>
          <dl className="grid grid-cols-2 gap-3">
            <Dato rotulo="AFP">{o(v.afp)}</Dato>
            <Dato rotulo="Salud">{o(v.salud)}</Dato>
            <Dato rotulo="Cuenta">{o(v.cuenta)}</Dato>
            <Dato rotulo="Sueldo base"><SueldoBase ficha={ficha} /></Dato>
          </dl>
        </div>
        <Link to={`${base}/ficha`} className="text-sm font-semibold text-primary hover:underline">
          Ver la ficha completa
        </Link>
      </div>
    </GlassPanel>
  )
}
